import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  RequestMagicLinkRequest,
  RequestMagicLinkResponse,
} from "@/api/requestMagicLink/types";
import { requestMagicLink } from "@/api/requestMagicLink/requestMagicLinkApi";

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;

  // Magic Link-specific state
  magicLinkLoading: boolean;
  magicLinkError: string | null;
  magicLinkSuccess: boolean;
  magicLinkMessage: string | null;
}

const initialState: AuthState = {
  token: localStorage.getItem("token"),
  refreshToken: localStorage.getItem("refreshToken"),
  status: "idle",
  error: null,

  // Magic Link-specific state
  magicLinkLoading: false,
  magicLinkError: null,
  magicLinkSuccess: false,
  magicLinkMessage: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Existing actions
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      localStorage.setItem("token", action.payload);
    },
    setRefreshToken: (state, action: PayloadAction<string>) => {
      state.refreshToken = action.payload;
      localStorage.setItem("refreshToken", action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.status = action.payload ? "loading" : "idle";
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.status = "failed";
    },
    setSuccess: (state) => {
      state.status = "succeeded";
      state.error = null;
    },
    logout: (state) => {
      state.token = null;
      state.refreshToken = null;
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      state.status = "idle";
      state.error = null;
    },
    // New magic link actions
    setMagicLinkLoading: (state, action: PayloadAction<boolean>) => {
      state.magicLinkLoading = action.payload;
    },
    setMagicLinkError: (state, action: PayloadAction<string | null>) => {
      state.magicLinkError = action.payload;
    },
    setMagicLinkSuccess: (state, action: PayloadAction<boolean>) => {
      state.magicLinkSuccess = action.payload;
    },
    setMagicLinkMessage: (state, action: PayloadAction<string | null>) => {
      state.magicLinkMessage = action.payload;
    },
  },
});

export const {
  setToken,
  setRefreshToken,
  setLoading,
  setError,
  setSuccess,
  logout,
  setMagicLinkLoading,
  setMagicLinkError,
  setMagicLinkSuccess,
  setMagicLinkMessage,
} = authSlice.actions;

// === Thunk for requesting a magic link ===
// Now accepts an optional object: { shouldSetLoading?: boolean; onSuccess?: () => void }
export const requestMagicLinkAction =
  (
    email: string,
    options?: {
      shouldSetLoading?: boolean;
      onSuccess?: () => void;
    },
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    const { shouldSetLoading = true, onSuccess } = options || {};

    try {
      if (shouldSetLoading) {
        dispatch(setMagicLinkLoading(true));
      }
      // Reset any previous magic link error/success
      dispatch(setMagicLinkError(null));
      dispatch(setMagicLinkSuccess(false));
      dispatch(setMagicLinkMessage(null));

      // Perform the API call
      const response: RequestMagicLinkResponse = await requestMagicLink({
        email,
      });

      // If the API indicates success, store the success + message
      if (response.data.success) {
        dispatch(setMagicLinkSuccess(true));
        dispatch(setMagicLinkMessage(response.data.message));
        // Call the callback if provided
        if (onSuccess) {
          onSuccess();
        }
      } else {
        // If the server returned success=false, treat it as an error
        dispatch(setMagicLinkError(response.data.message || "Unknown error."));
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        dispatch(setMagicLinkError(error.message));
      } else {
        dispatch(setMagicLinkError("Failed to request magic link."));
      }
    } finally {
      if (shouldSetLoading) {
        dispatch(setMagicLinkLoading(false));
      }
    }
  };

export default authSlice.reducer;
